export const ROUND_META = [
  { k: "telephonic", label: "Telephonic interview" },
  { k: "video", label: "Video interview" },
  { k: "coding", label: "Coding round" },
];
export const SKILLS = {
  telephonic: [["qualification", "Qualification & motivation"], ["communication", "Communication"]],
  video: [["role_fit", "Role fit"], ["behavioral", "Behavioural"], ["communication", "Communication"], ["qualification", "Qualification"]],
  coding: [["correctness", "Correctness"], ["code_quality", "Code quality"]],
};
export const DEFAULT_SKILLS = {
  telephonic: { qualification: 50, communication: 50 },
  video: { role_fit: 34, behavioral: 33, communication: 33, qualification: 0 },
  coding: { correctness: 50, code_quality: 50 },
};

const enabledKeys = (stages) => ROUND_META.map((r) => r.k).filter((k) => stages?.[k]);

// Split 100 across keys in proportion to `weights` (equal when all zero), fixing rounding on the largest.
export function normalise(weights, keys) {
  if (!keys.length) return {};
  const tot = keys.reduce((s, k) => s + (weights[k] || 0), 0);
  const out = Object.fromEntries(keys.map((k) => [k, tot > 0 ? Math.round(((weights[k] || 0) * 100) / tot) : Math.floor(100 / keys.length)]));
  const diff = 100 - keys.reduce((s, k) => s + out[k], 0);
  const top = keys.reduce((a, b) => (out[b] >= out[a] ? b : a), keys[0]);
  out[top] += diff;
  return out;
}

// Set one slider and spread the rest across the others so the group still totals 100.
export function rebalance(weights, keys, key, value) {
  const others = keys.filter((k) => k !== key);
  if (!others.length) return { [key]: 100 };
  const left = 100 - value;
  const share = normalise(Object.fromEntries(others.map((k) => [k, weights[k] || 0])), others);
  const out = { [key]: value };
  others.forEach((k) => { out[k] = Math.round((share[k] * left) / 100); });
  const diff = 100 - Object.values(out).reduce((a, b) => a + b, 0);
  const fix = others.reduce((a, b) => (out[b] > out[a] ? b : a), others[0]);
  out[fix] = Math.max(0, out[fix] + diff);
  return out;
}

export function defaultScoring(stages) {
  const keys = enabledKeys(stages);
  return { rounds: { telephonic: 0, video: 0, coding: 0, ...normalise({}, keys) }, skills: JSON.parse(JSON.stringify(DEFAULT_SKILLS)) };
}

export function fullScoring(value, stages) {
  const base = defaultScoring(stages);
  if (!value) return base;
  const keys = enabledKeys(stages);
  return {
    rounds: { telephonic: 0, video: 0, coding: 0, ...normalise(value.rounds || {}, keys) },
    skills: Object.fromEntries(Object.keys(SKILLS).map((r) => [r, normalise({ ...DEFAULT_SKILLS[r], ...(value.skills?.[r] || {}) }, SKILLS[r].map(([k]) => k))])),
  };
}

const PRESET_DEF = {
  balanced: { rounds: { telephonic: 1, video: 1, coding: 1 }, skills: DEFAULT_SKILLS },
  technical: {
    rounds: { telephonic: 15, video: 25, coding: 60 },
    skills: { telephonic: { qualification: 70, communication: 30 }, video: { role_fit: 50, behavioral: 20, communication: 10, qualification: 20 }, coding: { correctness: 70, code_quality: 30 } },
  },
  communication: {
    rounds: { telephonic: 40, video: 45, coding: 15 },
    skills: { telephonic: { qualification: 30, communication: 70 }, video: { role_fit: 20, behavioral: 30, communication: 50, qualification: 0 }, coding: { correctness: 50, code_quality: 50 } },
  },
};
export const PRESETS = [["balanced", "Balanced"], ["technical", "Technical focus"], ["communication", "Communication focus"]];
export const presetScoring = (name, stages) => fullScoring(PRESET_DEF[name], stages);
export { enabledKeys };

export function scoringSummary(value, stages) {
  const keys = enabledKeys(stages);
  if (!keys.length) return "";
  const r = fullScoring(value, stages).rounds;
  const short = { telephonic: "Phone", video: "Video", coding: "Coding" };
  return keys.map((k) => `${short[k]} ${r[k]}%`).join(" · ");
}
